/**
 * Generic single-chart report page used by Wealth, Manglik, Visa,
 * Business, Progeny, Legal — all of which have the same shape:
 * birth-form input → call API → render result with header + score
 * (optional) + multiple text sections + summary.
 *
 * Custom report pages (Career, BNN, Karmic, Health, Compat, Varshaphal,
 * Vidya, Sade Sati, Rectification, Career-Compat) have their own layouts.
 */
import { useCallback, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import type {
  ChartCreateRequest,
  InnerPracticeBlockEntry,
  SimpleSection,
  SpecialtyMethodEntry,
} from '@/api/types';
import { ChartInputForm } from '@/components/ChartInputForm';
import { useLang } from '@/i18n/useLang';

interface Bullet {
  hi: string;
  en: string;
}

interface SectionGroup {
  title_hi: string;
  title_en: string;
  /** Either a single text or a list of items */
  text_hi?: string;
  text_en?: string;
  bullets?: Bullet[];
}

export interface GenericReport {
  /** Numeric score 0..100, optional */
  score?: number;
  /** Map of structured sections to render generically */
  groups: SectionGroup[];
  sections?: SimpleSection[];
  summary_hi: string;
  summary_en: string;
  rules_version: string;
  // ── v2 additions (May 2026) ──────────────────────────────
  takeaways_hi?: string[];
  takeaways_en?: string[];
  methods?: SpecialtyMethodEntry[];
  inner_practice?: InnerPracticeBlockEntry | null;
  disclaimer_hi?: string;
  disclaimer_en?: string;
}

interface Props<T> {
  title_hi: string;
  title_en: string;
  subtitle_hi: string;
  subtitle_en: string;
  emoji: string;
  /** Adapter from backend response to GenericReport */
  fetchFn: (req: ChartCreateRequest) => Promise<T>;
  toReport: (data: T, lang: 'hi' | 'en') => GenericReport;
  /** Optional pre-form notice (e.g. medical disclaimer) */
  notice?: { hi: string; en: string };
  onBack: () => void;
}

export function GenericReportPage<T>({
  title_hi, title_en, subtitle_hi, subtitle_en, emoji,
  fetchFn, toReport, notice, onBack,
}: Props<T>) {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const reportMut = useMutation<T, Error, ChartCreateRequest>({
    mutationFn: fetchFn,
  });

  const onFormSubmit = useCallback(
    (req: ChartCreateRequest) => {
      reportMut.mutate(req);
    },
    [reportMut],
  );

  const submitError = useMemo(() => {
    if (!reportMut.error) return null;
    const e = reportMut.error;
    if (e instanceof ApiError) {
      if (e.status === 422) return t('अमान्य इनपुट', 'Invalid input');
    }
    return e.message ?? 'Error';
  }, [reportMut.error, lang]);

  const report = reportMut.data ? toReport(reportMut.data, lang) : null;

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 p-4 lg:gap-6 lg:p-6">
      <button
        type="button"
        onClick={onBack}
        className="self-start text-xs text-saffron-300 hover:underline"
      >
        ← {t('मेन्यू पर वापस', 'Back to menu')}
      </button>

      <header className="rounded-2xl bg-stone-900/80 px-4 py-3 ring-1 ring-stone-800">
        <h1 className="text-lg font-semibold text-saffron-100">
          {emoji} {t(title_hi, title_en)}
        </h1>
        <p className="mt-1 text-xs text-stone-400">
          {t(subtitle_hi, subtitle_en)}
        </p>
      </header>

      {notice && (
        <div className="rounded-2xl bg-amber-950/30 p-3 ring-1 ring-amber-900/50">
          <p className="text-sm text-amber-100">{t(notice.hi, notice.en)}</p>
        </div>
      )}

      <ChartInputForm
        onSubmit={onFormSubmit}
        isSubmitting={reportMut.isPending}
        submitError={submitError}
      />

      {reportMut.isPending && (
        <p className="rounded-2xl bg-stone-900/40 p-8 text-center text-stone-400 ring-1 ring-stone-800">
          {t('गणना हो रही है…', 'Computing…')}
        </p>
      )}

      {report && <ReportView report={report} lang={lang} t={t} />}
    </div>
  );
}

function ReportView({
  report,
  lang,
  t,
}: {
  report: GenericReport;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  const takeaways = lang === 'hi' ? report.takeaways_hi : report.takeaways_en;
  return (
    <article className="space-y-4">
      {report.score !== undefined && (
        <section className="rounded-2xl bg-gradient-to-br from-saffron-900/60 to-stone-900 p-6 ring-2 ring-saffron-700/60">
          <p className="text-xs uppercase tracking-wider text-saffron-300">
            {t('स्कोर', 'Score')}
          </p>
          <p className="mt-1 text-5xl font-bold text-saffron-100">
            {report.score}
            <span className="text-2xl text-stone-400"> / 100</span>
          </p>
        </section>
      )}

      {takeaways && takeaways.length > 0 && (
        <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-saffron-700/40">
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-saffron-300">
            {t('मुख्य निष्कर्ष', 'Key Takeaways')}
          </h3>
          <ul className="ml-5 list-disc space-y-2 text-sm text-stone-100">
            {takeaways.map((tk, i) => (
              <li key={i}>{tk}</li>
            ))}
          </ul>
        </section>
      )}

      {report.groups.map((g, i) => (
        <section
          key={i}
          className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800"
        >
          <h3 className="mb-2 text-base font-semibold text-saffron-200">
            {t(g.title_hi, g.title_en)}
          </h3>
          {g.text_hi && (
            <p className="text-sm leading-relaxed text-stone-100">
              {lang === 'hi' ? g.text_hi : g.text_en}
            </p>
          )}
          {g.bullets && (
            <ul className="space-y-1 text-sm text-stone-100">
              {g.bullets.map((b, j) => (
                <li key={j} className="flex gap-2">
                  <span className="text-saffron-400">●</span>
                  <span>{lang === 'hi' ? b.hi : b.en}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}

      {report.sections && report.sections.length > 0 && (
        <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
          <h3 className="mb-3 text-base font-semibold text-saffron-200">
            {t('विश्लेषण विवरण', 'Analysis details')}
          </h3>
          <ul className="space-y-3">
            {report.sections.map((s, i) => (
              <li
                key={i}
                className="rounded-md border-l-2 border-saffron-700/50 bg-stone-900/40 px-3 py-2"
              >
                <p className="text-sm font-medium text-saffron-300">
                  {lang === 'hi' ? s.label_hi : s.label_en}
                </p>
                <p className="mt-1 text-sm text-stone-200">
                  {lang === 'hi' ? s.text_hi : s.text_en}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="rounded-2xl bg-gradient-to-br from-saffron-900/40 to-stone-900 p-5 ring-1 ring-saffron-700/40">
        <h3 className="mb-2 text-base font-semibold text-saffron-200">
          {t('सारांश', 'Summary')}
        </h3>
        <p className="text-sm leading-relaxed text-stone-100">
          {lang === 'hi' ? report.summary_hi : report.summary_en}
        </p>
      </section>

      {report.inner_practice && (
        <InnerPracticeSection
          ip={report.inner_practice} lang={lang} t={t}
        />
      )}

      {report.methods && report.methods.length > 0 && (
        <section className="rounded-2xl bg-stone-900/40 p-4 text-xs text-stone-500 ring-1 ring-stone-800">
          <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-stone-500">
            {t('विधि एवं स्रोत', 'Methodology & Sources')}
          </h3>
          <ul className="space-y-1 leading-relaxed">
            {report.methods.map((m, i) => (
              <li key={i}>
                <span>{lang === 'hi' ? m.method_hi : m.method_en}</span>
                <span className="italic text-stone-600">
                  {' '}— {lang === 'hi' ? m.source_hi : m.source_en}
                </span>
              </li>
            ))}
          </ul>
          {(report.disclaimer_hi || report.disclaimer_en) && (
            <p className="mt-3 italic text-stone-600">
              {lang === 'hi' ? report.disclaimer_hi : report.disclaimer_en}
            </p>
          )}
        </section>
      )}

      <p className="text-center text-[10px] text-stone-600">
        {report.rules_version}
      </p>
    </article>
  );
}

function InnerPracticeSection({
  ip, lang, t,
}: {
  ip: InnerPracticeBlockEntry;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  return (
    <section className="rounded-2xl bg-emerald-950/30 p-5 ring-1 ring-emerald-800/40">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-emerald-200">
        {t('साधना (Inner Practice)', 'Inner Practice')}
      </h2>

      <div className="rounded-md bg-stone-900/40 p-3 ring-1 ring-stone-800">
        <p className="text-[10px] uppercase tracking-widest text-emerald-400">
          {t('इष्ट-देव जाप', 'Iṣṭa-deva recitation')}
        </p>
        <p className="mt-1 text-sm text-stone-200">
          {lang === 'hi' ? ip.ishta_deva.text_hi : ip.ishta_deva.text_en}
        </p>
      </div>

      {ip.bhavana_contemplations.length > 0 && (
        <div className="mt-3 space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-emerald-400">
            {t('चयनित भावनाएँ', 'Selected Bhāvanās')}
          </p>
          {ip.bhavana_contemplations.map((bv) => (
            <div key={bv.bhavana_id} className="rounded-md bg-stone-900/40 p-3 ring-1 ring-stone-800">
              <p className="text-sm font-semibold text-stone-200">
                {lang === 'hi' ? bv.title_hi : bv.title_en}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-stone-400">
                {lang === 'hi' ? bv.theme_hi : bv.theme_en}
              </p>
              <p className="mt-2 text-sm italic text-emerald-300">
                "{lang === 'hi' ? bv.affirmation_hi : bv.affirmation_en}"
              </p>
            </div>
          ))}
        </div>
      )}

      {ip.affirmations.length > 0 && (
        <div className="mt-3 space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-emerald-400">
            {t('भावना-योग संकल्प', 'Bhāvanā Yoga Affirmations')}
          </p>
          {ip.affirmations.map((af, i) => (
            <div key={i} className="rounded-md bg-stone-900/40 p-3 ring-1 ring-stone-800">
              <p className="text-[10px] uppercase tracking-wider text-stone-500">
                {lang === 'hi' ? af.chart_signal_hi : af.chart_signal_en}
              </p>
              <p className="mt-1 text-sm italic text-emerald-200">
                "{lang === 'hi' ? af.affirmation_hi : af.affirmation_en}"
              </p>
            </div>
          ))}
        </div>
      )}

      <p className="mt-3 rounded-md bg-stone-900/40 p-2 text-xs italic text-stone-300">
        {lang === 'hi' ? ip.practice_instruction_hi : ip.practice_instruction_en}
      </p>
    </section>
  );
}
